import { AmbientIllustration } from "@solomon-apps/shared/src/components/AmbientIllustration";
import React from "react";

import { type GraphSlot, graphSlotForIndex } from "../graphColors";
import { TOTAL_SLOT } from "../reportingSeries";

/**
 * Reports assembling, abstractly: a preview of the three Reporting charts
 * building on one baseline. A rate line draws in segment by segment, stacked
 * bars rise, and stacked bands swell up over faint gridlines. Each piece
 * dissolves late in the cycle and the next pass rebuilds it.
 *
 * Colours come from the graph palette in the same order the real charts use:
 * the line takes the portfolio-total slot (as Rewards Rate does), the bar
 * segments and bands take the first three position slots (as Earnings and
 * Balances stack them). No Ember, no status colours.
 *
 * Transform and opacity only. `AmbientIllustration` pauses the loop off
 * screen and holds the assembled frame for reduced motion.
 *
 * Coordinates are illustration units (viewBox), not UI dimensions. The
 * viewBox is cropped to the art: the tallest assembled piece (the last bar)
 * tops out below the first gridline, with a small margin above it.
 */

const WIDTH = 720;
const HEIGHT = 180;
const BASELINE = 172;
const GRIDLINES = [12, 52, 92, 132];

/** Position slots, in the real charts' order (first position = deepest). */
const POSITION_SLOTS: readonly GraphSlot[] = [0, 1, 2].map(graphSlotForIndex);

const FILL: Record<GraphSlot, string> = {
    primary: "fill-graph-primary",
    secondary: "fill-graph-secondary",
    tertiary: "fill-graph-tertiary",
    quaternary: "fill-graph-quaternary",
    quinary: "fill-graph-quinary",
    senary: "fill-graph-senary",
    septenary: "fill-graph-septenary",
    octonary: "fill-graph-octonary",
    nonary: "fill-graph-nonary",
    denary: "fill-graph-denary"
};
const STROKE: Record<GraphSlot, string> = {
    primary: "stroke-graph-primary",
    secondary: "stroke-graph-secondary",
    tertiary: "stroke-graph-tertiary",
    quaternary: "stroke-graph-quaternary",
    quinary: "stroke-graph-quinary",
    senary: "stroke-graph-senary",
    septenary: "stroke-graph-septenary",
    octonary: "stroke-graph-octonary",
    nonary: "stroke-graph-nonary",
    denary: "stroke-graph-denary"
};

type Point = readonly [number, number];

const RATE_POINTS: readonly Point[] = [
    [48, 118],
    [78, 102],
    [108, 110],
    [138, 82],
    [168, 90],
    [198, 60],
    [224, 70]
];

/** Bar totals rise; each splits into three positions, base position largest. */
const BAR_TOTALS = [40, 58, 76, 96, 118, 142];
const BAR_SHARES = [0.55, 0.3, 0.15];
const BAR_X = 272;
const BAR_STEP = 30;
const BAR_WIDTH = 18;
/** Paper showing between stacked segments: a hairline at rendered size. */
const SEGMENT_GAP = 1.5;

const AREA_X = [496, 540, 584, 628, 672];
/** Top edge of each band, base band first. */
const BAND_TOPS: readonly (readonly number[])[] = [
    [150, 144, 138, 128, 120],
    [134, 124, 118, 104, 94],
    [122, 108, 100, 80, 62]
];

function polyline(points: readonly Point[]): string {
    return points
        .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x} ${y}`)
        .join("");
}

function edge(tops: readonly number[]): Point[] {
    return AREA_X.map((x, i) => [x, tops[i]] as const);
}

function band(top: readonly Point[], bottom: readonly Point[]): string {
    const back = bottom.slice().reverse();
    return `${polyline(top)}${back.map(([x, y]) => `L${x} ${y}`).join("")}Z`;
}

const FLOOR: Point[] = AREA_X.map((x) => [x, BASELINE] as const);

/** Per-piece start offset within the shared cycle. */
function delay(seconds: number): React.CSSProperties {
    return { "--rpt-delay": `${seconds.toFixed(2)}s` } as React.CSSProperties;
}

export const REPORTS_ASSEMBLING_CSS = `
.rpt-assembling { --rpt-cycle: 10s; }
.rpt-piece { opacity: 0; }
.rpt-fade { animation: rpt-fade var(--rpt-cycle) var(--ease-out-cubic) var(--rpt-delay, 0s) infinite; }
.rpt-rise {
    transform-box: view-box;
    transform-origin: 0 ${BASELINE}px;
    animation: rpt-rise var(--rpt-cycle) var(--ease-out-cubic) var(--rpt-delay, 0s) infinite;
}
@keyframes rpt-fade {
    0% { opacity: 0; }
    8%, 70% { opacity: 1; }
    82%, 100% { opacity: 0; }
}
@keyframes rpt-rise {
    0% { opacity: 0; transform: scaleY(0); }
    10%, 70% { opacity: 1; transform: scaleY(1); }
    82%, 100% { opacity: 0; transform: scaleY(1); }
}
[data-motion="static"] .rpt-piece { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) {
    .rpt-piece { opacity: 1; transform: none; }
}
`;

const LINE_STEP = 0.16;

export function ReportsAssemblingIllustration({
    className
}: {
    className?: string;
}): React.ReactElement {
    return (
        <AmbientIllustration css={REPORTS_ASSEMBLING_CSS} className={className}>
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio="xMidYMax meet"
                className="rpt-assembling block h-auto w-full"
                fill="none"
                data-testid="reports-assembling"
            >
                {/* Gridlines and baseline: the static frame the pieces build on. */}
                <g className="stroke-line-base" strokeWidth={1}>
                    {GRIDLINES.map((y) => (
                        <path key={y} d={`M24 ${y}H696`} opacity={0.6} />
                    ))}
                    <path d={`M24 ${BASELINE}H696`} />
                </g>

                {/* Rewards Rate: one hue, drawn segment by segment; a marker
                    lands at each vertex as the segment reaching it appears. */}
                <g
                    className={STROKE[TOTAL_SLOT]}
                    strokeWidth={1.5}
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                >
                    {RATE_POINTS.slice(1).map(([x, y], i) => {
                        const [px, py] = RATE_POINTS[i];
                        return (
                            <path
                                key={x}
                                d={`M${px} ${py}L${x} ${y}`}
                                className="rpt-piece rpt-fade"
                                style={delay(0.2 + i * LINE_STEP)}
                                data-ambient-motion
                            />
                        );
                    })}
                </g>
                {/* Markers in the accent (Ember): the one earned accent on the
                    page, marking each rate reading. */}
                <g className="fill-accent-base">
                    {RATE_POINTS.map(([x, y], i) => (
                        <rect
                            key={x}
                            x={x - 2}
                            y={y - 2}
                            width={4}
                            height={4}
                            className="rpt-piece rpt-fade"
                            style={delay(
                                0.2 +
                                    Math.max(0, i - 1) * LINE_STEP +
                                    (i === 0 ? 0 : LINE_STEP / 2)
                            )}
                            data-ambient-motion
                        />
                    ))}
                </g>

                {/* Earnings: stacked bars, solid segments with a hairline gap. */}
                {BAR_TOTALS.map((total, barIndex) => {
                    const x = BAR_X + barIndex * BAR_STEP;
                    let bottom = BASELINE;
                    return (
                        <g key={x}>
                            {BAR_SHARES.map((share, segment) => {
                                const height = Math.round(total * share);
                                const gap = segment === 0 ? 0 : SEGMENT_GAP;
                                const y = bottom - gap - height;
                                bottom = y;
                                return (
                                    <rect
                                        key={segment}
                                        x={x}
                                        y={y}
                                        width={BAR_WIDTH}
                                        height={height}
                                        className={`rpt-piece rpt-rise ${FILL[POSITION_SLOTS[segment]]}`}
                                        style={delay(
                                            1.1 +
                                                barIndex * 0.12 +
                                                segment * 0.14
                                        )}
                                        data-ambient-motion
                                    />
                                );
                            })}
                        </g>
                    );
                })}

                {/* Balances: stacked bands, each a tint with a 1px edge in its hue. */}
                {BAND_TOPS.map((tops, index) => {
                    const top = edge(tops);
                    const under =
                        index === 0 ? FLOOR : edge(BAND_TOPS[index - 1]);
                    const slot = POSITION_SLOTS[index];
                    const style = delay(2.3 + index * 0.3);
                    return (
                        <g key={slot}>
                            <path
                                d={band(top, under)}
                                className={`rpt-piece rpt-rise ${FILL[slot]}`}
                                fillOpacity={0.55}
                                style={style}
                                data-ambient-motion
                            />
                            <path
                                d={polyline(top)}
                                className={`rpt-piece rpt-rise ${STROKE[slot]}`}
                                strokeWidth={1}
                                style={style}
                                data-ambient-motion
                            />
                        </g>
                    );
                })}
            </svg>
        </AmbientIllustration>
    );
}
